import { Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { formatDistance } from 'date-fns';
import {
  formatCompanyActive,
  invoiceModeLabel,
  weeklyInvoiceDayLabel,
} from 'src/constants/distributor-invoicing';
import { CompanyService } from 'src/core/services/company.service';
import { LocalStorageService } from 'src/core/services/local-storage.service';
import { Company, CompanyType } from 'src/interfaces/company';
import { FormControl } from '@angular/forms';
import { FormBuilder } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { format } from 'date-fns';


@Component({
  selector: 'core-company-list',
  templateUrl: './company-list.component.html',
  styleUrls: ['./company-list.component.scss'],
})
export class CompanyListComponent implements OnInit {
  allCompanyTypes = '';

  companyTypes: CompanyType[] = [];
  companyType = new FormControl('');
  companySearch = new FormControl('');
  companiesDataSource: MatTableDataSource<Company>;
  page: number = 1;
  limit: number = 15;
  totalNumberOfCompanies: number = 0;
  sortDirection: string = 'asc';
  orderBy: string = 'name';

  companyFilterForm = this._formBuilder.group({
    companyType: [''],
    search: [''],
  });

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  /** Distributor list: company type filter DIS (same route as companies). */
  get showDistributorColumns(): boolean {
    return this.companyFilterForm?.value?.companyType === 'DIS';
  }

  get tableColumns(): string[] {
    if (this.showDistributorColumns) {
      return [
        'name',
        'invoice_mode',
        'weekly_invoice_day',
        'is_active',
        'email',
        'phone_number',
        'sage',
        'created_at',
        'updated_at',
      ];
    }
    return [
      'name',
      'type',
      'email',
      'phone_number',
      'sage',
      'created_at',
      'updated_at',
    ];
  }

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _companyService: CompanyService,
    private _localStorageService: LocalStorageService,
    private _formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    const limit = this._localStorageService.get('COMPANIES', 'LIMIT');

    if (limit) {
      this.limit = parseInt(limit);
    }

    this._route.data.subscribe((data) => {
      this.companiesDataSource = new MatTableDataSource<Company>(
        data.companies.companies
      );
      this.companyTypes = data.companies.companyTypes;
      this.companiesDataSource.sort = this.sort;
      this.totalNumberOfCompanies =
        this._companyService.getTotalNumberOfCompanies();
      this.page = data.companies.pageNumber;

      // Initialize filters from query params
      const qp = this._route.snapshot.queryParamMap;
      const qpSearch = qp.get('search');
      const qpCompanyType = qp.get('companyType');

      if (qpSearch) {
        this.companyFilterForm.controls['search'].setValue(qpSearch, {
          emitEvent: false,
        });
      }
      if (qpCompanyType) {
        this.companyFilterForm.controls['companyType'].setValue(qpCompanyType, {
          emitEvent: false,
        });
      }

      if (qpSearch || qpCompanyType) {
        (async () => {
          this.page = 1;
          this.listCompanies(
            this.page,
            this.limit,
            this.companyFilterForm.value.search
              ? this.companyFilterForm.value.search
              : '',
            this.companyFilterForm.value.companyType
              ? this.companyFilterForm.value.companyType
              : null,
            this.sortDirection,
            this.orderBy
          );
        })();
      }
    });

    this.companyFilterForm.controls['companyType'].valueChanges.subscribe(
      (value) => {
        (async () => {
          this.page = 1;
          this.listCompanies(
            this.page,
            this.limit,
            this.companyFilterForm.value.search
              ? this.companyFilterForm.value.search
              : '',
            value ? value : '',
            this.sortDirection,
            this.orderBy
          );
        })();
      }
    );


    this.companyFilterForm.controls['search'].valueChanges.subscribe(
      (value) => {
        (async () => {
          this.page = 1;
          this.listCompanies(
            this.page,
            this.limit,
            value ? value : '',
            this.companyFilterForm.value.companyType
              ? this.companyFilterForm.value.companyType
              : null,
            this.sortDirection,
            this.orderBy
          );
        })();
      }
    );
  }

  onPageChange(event: PageEvent) {
    // Get more batches
    this.limit = event.pageSize;
    this.page = event.pageIndex + 1;
    this._localStorageService.store(
      'COMPANIES',
      'LIMIT',
      this.limit.toString()
    );
    this._localStorageService.store('COMPANIES', 'PAGE', this.page.toString());

    (async () => {
      this.listCompanies(
        this.page,
        this.limit,
        this.companyFilterForm.value.search
          ? this.companyFilterForm.value.search
          : '',
        this.companyFilterForm.value.companyType
          ? this.companyFilterForm.value.companyType
          : null
      );
    })();
  }

  formatTime(time: string | null) {
    if (!time) return '-';
    return format(new Date(time), 'MMMM dd, yyyy HH:mm');
  }

  openCompany(company: Company): void {
    this._router.navigate(['portal/companies/view', company.id], {
      queryParams: {
        search: this.companyFilterForm.value.search || undefined,
        companyType: this.companyFilterForm.value.companyType || undefined,
      },
    });
  }

  getType(company: Company): string {
    const companyType: CompanyType | undefined = this.companyTypes.find(
      (type) => type.id === company.company_type_id
    );
    return companyType ? companyType.type : '-';
  }

  /** Sage row uses same field as company view (normalized on GET). */
  isOnSage(company: Company): boolean {
    return !!(company?.sage_contact_id && String(company.sage_contact_id).trim());
  }

  rowInvoiceMode(company: Company): string {
    return invoiceModeLabel(company.invoice_mode);
  }

  rowWeeklyDay(company: Company): string {
    return weeklyInvoiceDayLabel(company.weekly_invoice_day);
  }

  rowActive(company: Company): string {
    return formatCompanyActive(company.is_active);
  }

  async sortCompanies(sort: Sort) {
    switch (sort.active) {
      case 'name':
        this.orderBy = 'name';
        break;
      default:
        this.orderBy = 'name';
        break;
    }
    this.sortDirection = sort.direction;
    this.page = 1;
    const newClients = this.listCompanies(
      this.page,
      this.limit,
      this.companyFilterForm.value.search
        ? this.companyFilterForm.value.search
        : '',
      this.companyFilterForm.value.companyType
        ? this.companyFilterForm.value.companyType
        : null,
      this.sortDirection,
      this.orderBy
    );
  }

  listCompanies(
    page: number = 1,
    limit: number = 10,
    search: string = '',
    type: string | null = null,
    sortDirection: string = 'asc',
    orderBy: string = 'name'
  ) {
    (async () => {
      const newCompanies = await this._companyService.getCompanies(
        page,
        limit,
        search,
        type,
        sortDirection,
        orderBy
      );
      const newData = [...newCompanies];
      this.companiesDataSource.data = newData;
      this.totalNumberOfCompanies =
        this._companyService.getTotalNumberOfCompanies();
    })();
  }
}
